// src/store/features/articleSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IArticle } from "../../types/article";
import {
  articleService,
  CreateArticlePayload,
  UpdateArticlePayload,
} from "../../services/api/articleService";

// Define the state type
interface ArticleState {
  articles: IArticle[];
  currentArticle: IArticle | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: ArticleState = {
  articles: [],
  currentArticle: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchArticles = createAsyncThunk(
  "articles/fetchArticles",
  async (_, { rejectWithValue }) => {
    try {
      return await articleService.getAllArticles();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchArticleBySlug = createAsyncThunk(
  "articles/fetchArticleBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      return await articleService.getArticleBySlug(slug);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchArticlesByCategory = createAsyncThunk(
  "articles/fetchArticlesByCategory",
  async (category: IArticle["category"], { rejectWithValue }) => {
    try {
      return await articleService.getArticlesByCategory(category);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createArticle = createAsyncThunk(
  "articles/createArticle",
  async (articleData: CreateArticlePayload, { rejectWithValue }) => {
    try {
      return await articleService.createArticle(articleData);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateArticle = createAsyncThunk(
  "articles/updateArticle",
  async (
    {
      articleId,
      articleData,
    }: { articleId: string; articleData: UpdateArticlePayload },
    { rejectWithValue }
  ) => {
    try {
      return await articleService.updateArticle(articleId, articleData);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteArticle = createAsyncThunk(
  "articles/deleteArticle",
  async (articleId: string, { rejectWithValue }) => {
    try {
      await articleService.deleteArticle(articleId);
      return articleId;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Create the slice
const articleSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    clearCurrentArticle: (state) => {
      state.currentArticle = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all articles
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchArticles.fulfilled,
        (state, action: PayloadAction<IArticle[]>) => {
          state.loading = false;
          state.articles = action.payload;
        }
      )
      .addCase(fetchArticles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch article by slug
    builder
      .addCase(fetchArticleBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchArticleBySlug.fulfilled,
        (state, action: PayloadAction<IArticle>) => {
          state.loading = false;
          state.currentArticle = action.payload;
        }
      )
      .addCase(fetchArticleBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch articles by category
    builder
      .addCase(fetchArticlesByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchArticlesByCategory.fulfilled,
        (state, action: PayloadAction<IArticle[]>) => {
          state.loading = false;
          state.articles = action.payload;
        }
      )
      .addCase(fetchArticlesByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create article
    builder
      .addCase(createArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createArticle.fulfilled,
        (state, action: PayloadAction<IArticle>) => {
          state.loading = false;
          state.articles.push(action.payload);
        }
      )
      .addCase(createArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update article
    builder
      .addCase(updateArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateArticle.fulfilled,
        (state, action: PayloadAction<IArticle>) => {
          state.loading = false;
          const index = state.articles.findIndex(
            (article) => article.articleId === action.payload.articleId
          );
          if (index !== -1) {
            state.articles[index] = action.payload;
          }
          if (state.currentArticle?.articleId === action.payload.articleId) {
            state.currentArticle = action.payload;
          }
        }
      )
      .addCase(updateArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete article
    builder
      .addCase(deleteArticle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteArticle.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.articles = state.articles.filter(
            (article) => article.articleId !== action.payload
          );
          if (state.currentArticle?.articleId === action.payload) {
            state.currentArticle = null;
          }
        }
      )
      .addCase(deleteArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { clearCurrentArticle, clearError } = articleSlice.actions;
export default articleSlice.reducer;

// Selectors
export const selectArticles = (state: { articles: ArticleState }) =>
  state.articles.articles;
export const selectCurrentArticle = (state: { articles: ArticleState }) =>
  state.articles.currentArticle;
export const selectArticlesLoading = (state: { articles: ArticleState }) =>
  state.articles.loading;
export const selectArticlesError = (state: { articles: ArticleState }) =>
  state.articles.error;
